import _ from 'lodash';
import { flux } from '@/core/flux.module';
import { getStorageSafely } from '@/utilities/storage.utilities';
import type { StoreApi } from 'zustand';

export let reduxDevtoolsConnection: ReturnType<ReduxDevtoolsExtension['connect']> | undefined;

export let isDebugStoresEnabled: boolean | undefined;

const zustandStoreInstances: Record<string, StoreApi<unknown>> = {};

export const registerZustandDevtoolsStore = (storeName: string, storeInstance: StoreApi<unknown>) => {
  zustandStoreInstances[storeName] = storeInstance;
};

export const getAllDevtoolsStoreStates = () => ({
  ...(flux.dispatcher.storeInstances as Record<string, unknown>),
  ..._.mapValues(zustandStoreInstances, (store) => ({ state: store.getState() })),
});

export const sendDevtoolsState = (action: Action) => {
  reduxDevtoolsConnection?.send(action, getAllDevtoolsStoreStates());
};

export function isDebugStoresDevtoolsEnabled() {
  if (process.env.NODE_ENV === 'test' || typeof window === 'undefined') {
    return false;
  }

  const debugStoresLocalStorage = getStorageSafely().getItem('debug-stores') !== null;
  const debugStoresSearchParam =
    new URLSearchParams(window.location.search).get('debug-stores')?.trim().toLowerCase() ?? '';

  return debugStoresSearchParam === 'true' || debugStoresLocalStorage;
}

export const stateSanitizer = (stores: any) =>
  _.reduce(stores, (allStoresState, store, storeName) => ({ ...allStoresState, [storeName]: store.state }), {});

export const initializeReduxDevtools = () => {
  if (process.env.NODE_ENV === 'test') {
    return;
  }

  const debugStoresSearchParam = _.lowerCase(new URLSearchParams(window.location.search).get('debug-stores') ?? '');

  if (debugStoresSearchParam && debugStoresSearchParam !== 'true') {
    return getStorageSafely().removeItem('debug-stores');
  }

  const debugStoresLocalStorage = Boolean(getStorageSafely().getItem('debug-stores'));

  if (debugStoresSearchParam === 'true' || debugStoresLocalStorage) {
    isDebugStoresEnabled = true;

    // early return if the devtools connection is already established
    if (reduxDevtoolsConnection) {
      return;
    }

    reduxDevtoolsConnection = window.__REDUX_DEVTOOLS_EXTENSION__?.connect({
      name: 'All stores',
      stateSanitizer,
    });
    reduxDevtoolsConnection?.init(getAllDevtoolsStoreStates());

    reduxDevtoolsConnection?.subscribe((data) => {
      if (data?.type !== 'DISPATCH' || data.payload?.type === 'PAUSE_RECORDING') {
        return;
      }

      switch (data.payload?.type) {
        case 'COMMIT':
          reduxDevtoolsConnection?.init(getAllDevtoolsStoreStates());
          break;
        case 'RESET':
          reduxDevtoolsConnection?.init({});
          console.error('Redux DevTools action not fully supported: RESET');
          break;
        default:
          console.error(`Redux DevTools action not fully supported: ${data.payload?.type ?? 'unknown'}`);
          break;
      }
    });

    if (!debugStoresLocalStorage) {
      getStorageSafely().setItem('debug-stores', 'true');
    }
  }
};
